import { NETWORK_SUMMARY, NETWORK_FOUNDING, NETWORK_SEPARATE, NETWORK_TAGLINE } from "@/lib/network";
import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeading, CTASection } from "@/components/layout";
import { WaitlistForm } from "@/components/kit-forms";
import { KIT_TAGS } from "@/lib/kit.functions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";



const TITLE = "Trust Operating System | Tables and Bridges";
const DESC =
  "The Trust Operating System is the foundational methodology of Tables and Bridges, helping law enforcement agencies diagnose, build, and prove community trust.";

export const Route = createFileRoute("/trust-operating-system")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/trust-operating-system" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/trust-operating-system" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: TOSPage,
});


const ARCHITECTURE: Array<{
  name: string;
  question: string;
  desc: string;
  pathway?: string[];
}> = [
  {
    name: "Communities of Trust",
    question: "Where is trust experienced?",
    desc: "The relational environments where trust must exist, including the individual, peers, leaders, the community served, and the shared Table.",
  },
  {
    name: "The Five Trust Spaces",
    question: "How is trust experienced?",
    desc: "Internal, Relational, Structural, Narrative, and Behavioral conditions that shape whether trust strengthens or fractures.",
  },
  {
    name: "Bridge Dynamics",
    question: "What can the relationship currently carry?",
    desc: "A structured examination of Strength, Stability, Capacity, and Movement.",
  },
  {
    name: "The Trust Engine",
    question: "How does trust move from fracture to sustainment?",
    desc: "A staged pathway that carries a relationship from diagnosis through sustained impact.",
    pathway: ["Diagnose", "Stabilize", "Align", "Build", "Transfer", "Prove", "Sustain"],
  },
  {
    name: "Tables and Bridges",
    question: "How is trust generated and moved?",
    desc: "TABLES creates the conditions for trust. BRIDGES carries trust into decisions, systems, accountability, and lasting change.",
  },
  {
    name: "PPP+ Lens",
    question: "What level of intervention is required?",
    desc: "Program. Practice. Policy. Power.",
  },
  {
    name: "Trust Evidence System",
    question: "How do we know anything changed?",
    desc: "A system for documenting activity, commitments, behavior, outcomes, limitations, and next decisions.",
  },
];

const FAQS: Array<{ q: string; a: string }> = [

  {
    q: "Is the Trust Operating System a training program?",
    a: "It is a methodology, not a single course. Training is one way to access it. Agencies can also engage assessments, consulting, facilitation, and implementation support built on the same system.",
  },
  {
    q: "Who is it built for?",
    a: "Community Engagement professionals, their supervisors, training leaders, command staff, and any agency leader responsible for public trust and community partnership.",
  },
  {
    q: "How is it different from community policing training?",
    a: "Community policing training often focuses on tactics and programs. The Trust Operating System focuses on the conditions that make trust possible, the relationships that carry it, and the evidence that proves it is improving.",
  },
  {
    q: "Can an agency adopt part of the system, or does it require everything?",
    a: "Agencies can begin where they are ready. The components are designed to work together, but engagements are scoped to the agency's condition, authority, and desired outcome.",
  },
];



function TOSPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-warm-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-25">
          <svg className="w-full h-full" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" fill="none">
            <path d="M0 500 L360 340 L720 420 L1080 260 L1440 340" stroke="#F2B84B" strokeWidth="1" fill="none" />
            <path d="M0 560 L360 400 L720 480 L1080 320 L1440 400" stroke="#F7F5EF" strokeOpacity="0.4" strokeWidth="0.5" />
            <circle cx="360" cy="340" r="3" fill="#F2B84B" />
            <circle cx="720" cy="420" r="3" fill="#F2B84B" />
            <circle cx="1080" cy="260" r="3" fill="#F2B84B" />
          </svg>
        </div>
        <div className="container-editorial relative z-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <p className="eyebrow mb-8">The Trust Operating System</p>
          <h1 className="max-w-5xl font-display text-[clamp(2.5rem,5.4vw,5rem)] leading-[1] tracking-[-0.02em]">
            Trust is not an activity.
            <br />
            <span className="italic text-gold">It is an operating condition.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-warm-white/85 text-lg leading-relaxed">
            The Trust Operating System is the foundational methodology of Tables &amp;
            Bridges. It helps law enforcement leaders and Community Engagement
            professionals understand where trust lives, where it is weakening, which
            relationships must be strengthened, what level of intervention is required,
            and how progress can be demonstrated.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <a href="/for-agencies" className="btn-base btn-primary">Bring the System to Your Agency</a>
          </div>
        </div>
      </section>

      {/* WHY */}
      <Section tone="warm-white">
        <SectionHeading
          eyebrow="Why an operating system"
          title="Good intentions are not a trust strategy."
        />
        <div className="mt-10 grid gap-8 lg:grid-cols-2 max-w-5xl text-navy/85 leading-relaxed">
          <p>
            Agencies frequently invest in events, messaging, surveys, partnerships, and
            listening sessions. Those efforts may create visibility, access, or
            temporary connection.
          </p>
          <p>
            They do not automatically create stronger trust. The Trust Operating System
            helps agencies connect activity to diagnosis, authority, ownership,
            evidence, and sustainment.
          </p>
        </div>
      </Section>

      {/* ARCHITECTURE */}
      <Section tone="ink">
        <SectionHeading
          eyebrow="Architecture"
          title="A connected system, not a collection of tools."
          lede="Seven components that work together to make trust legible, measurable, and improvable."
        />
        <div className="mt-14 space-y-px bg-warm-white/10">
          {ARCHITECTURE.map((a, i) => (
            <article
              key={a.name}
              className="bg-ink px-6 md:px-10 py-10 md:py-12 grid gap-6 md:grid-cols-[auto_1fr] items-start"
            >
              <div className="flex items-center gap-4 md:min-w-[9rem]">
                <span className="font-display text-gold text-4xl md:text-5xl leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div>
                <h3 className="font-display text-warm-white text-2xl md:text-3xl">
                  {a.name}
                </h3>
                <p className="mt-3 text-gold text-sm font-semibold uppercase tracking-[0.15em]">
                  {a.question}
                </p>
                <p className="mt-4 text-warm-white/80 leading-relaxed measure-body">
                  {a.desc}
                </p>
                {a.pathway && (
                  <ol className="mt-6 flex flex-wrap gap-2">
                    {a.pathway.map((step, idx) => (
                      <li
                        key={step}
                        className="flex items-center gap-2 text-xs text-warm-white/85"
                      >
                        <span className="tag-pill !py-1.5 !px-3">{step}</span>
                        {idx < a.pathway!.length - 1 && (
                          <span aria-hidden className="text-gold">
                            →
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* THE STANDARD */}
      <Section tone="deep-green">
        <div className="max-w-4xl">
          <p className="eyebrow mb-6">The Standard</p>
          <p className="font-display text-3xl md:text-5xl leading-tight text-warm-white">
            The question is not whether people attended.
          </p>
          <p className="font-display text-3xl md:text-5xl leading-tight italic text-gold mt-2">
            The question is whether trust became stronger because the work occurred.
          </p>
          <span aria-hidden className="divider-gold mt-10" />
          <p className="mt-8 text-warm-white/85 text-lg leading-relaxed measure-body">
            The Trust Operating System helps practitioners and agencies move from
            visible activity to credible trust infrastructure.
          </p>
        </div>
      </Section>

      {/* WHAT IT IS NOT */}
      <Section tone="warm-white">
        <SectionHeading eyebrow="Clarify" title="What the system is not." />
        <ul className="mt-10 grid gap-4 md:grid-cols-2 max-w-4xl">
          {[
            "Not a public-relations campaign",
            "Not an event-planning framework",
            "Not a generic satisfaction survey",
            "Not a promise that every conflict will disappear",
            "Not a substitute for leadership responsibility",
            "Not a collection of worksheets without implementation",
          ].map((n) => (
            <li key={n} className="flex gap-3 text-navy/85 border-t border-navy/10 pt-4">
              <span aria-hidden className="text-gold font-semibold">✕</span>
              {n}
            </li>
          ))}
        </ul>
      </Section>

      {/* TAKE THE WORK FURTHER */}
      <Section tone="navy">
        <div className="max-w-4xl">
          <p className="eyebrow mb-5 uppercase">TAKE THE WORK FURTHER</p>
          <div className="space-y-5 text-warm-white/85 leading-relaxed text-base md:text-lg mb-10 md:mb-12">
            <p>
              The Trust Operating System is a framework. What you do with it determines whether trust becomes stronger.
            </p>
            <p>
              Tables and Bridges delivers the full implementation of this methodology through agency training, facilitated assessments, consulting, and professional development. The CEO Practitioner Network, the professional network for Community Engagement officers, civilian staff, and supervisors, is built on the same system: practitioner to practitioner learning, applied training, and shared resources.
            </p>
            <p>
              If you are ready to move from understanding the framework to applying it, there are two ways forward.
            </p>
          </div>
          <div className="grid gap-6 md:gap-8 md:grid-cols-2">
            <div className="border-l-2 border-gold pl-4 md:pl-6">
              <p className="text-warm-white/85 leading-relaxed text-base md:text-lg mb-4 md:mb-5">
                Request an assessment and bring the Trust Operating System to your team.
              </p>
              <a href="/for-agencies" className="btn-base btn-primary w-full text-center whitespace-normal sm:w-auto">
                Work With Tables and Bridges
              </a>
            </div>
            <div className="border-l-2 border-gold pl-4 md:pl-6">
              <p className="text-warm-white/85 leading-relaxed text-base md:text-lg mb-4 md:mb-5">
                <strong className="block font-display text-warm-white text-xl mb-1">CEO Practitioner Network</strong>{NETWORK_TAGLINE} {NETWORK_SUMMARY} {NETWORK_FOUNDING} {NETWORK_SEPARATE}
              </p>
              <a
                href="https://ceopractitionernetwork.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base btn-primary w-full text-center whitespace-normal sm:w-auto"
              >
                Explore the Network
              </a>
            </div>
          </div>

          <div className="mt-14 md:mt-16">
            <p className="eyebrow mb-5 uppercase">Common questions</p>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={"tos-faq-" + i} className="border-warm-white/20">
                  <AccordionTrigger className="text-left font-display text-warm-white text-base md:text-lg hover:no-underline py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-warm-white/80 text-sm md:text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>


      {/* FREE GUIDE */}
      <Section tone="deep-green">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Coming Soon</p>
            <h2 className="font-display text-warm-white text-[clamp(2rem,4vw,3.25rem)] leading-[1.02]">
              Why Engagement Fails{" "}
              <span className="text-gold italic">- And How to Fix It.</span>
            </h2>
            <span aria-hidden className="divider-gold mt-8" />
            <p className="mt-8 text-warm-white/85 text-lg leading-relaxed measure-body">
              A practitioner&apos;s guide to diagnosing trust conditions is coming
              soon. Enter your email to be the first to receive it.
            </p>
          </div>
          <div className="lg:col-span-5 border border-warm-white/25 bg-navy/40 p-8 md:p-10">
            <p className="text-warm-white/85 text-[15px] leading-relaxed">
              We will send the guide the day it is released, along with the field
              notes that shaped it.
            </p>
            <div className="mt-7">
              <WaitlistForm
                tag={KIT_TAGS.guideWaitlist}
                ctaLabel="Notify Me"
                placeholder="Your work email"
                variant="onGreen"
                successMessage="You are on the list. The guide will land in your inbox first."
              />
            </div>
          </div>
        </div>
      </Section>

      <CTASection
        title="Build trust with more than good intentions."
        primary={{ label: "Work With Tables and Bridges", href: "https://tablesandbridges.com/for-agencies" }}
        tone="ink"
      />

    </div>
  );
}
